import camelcase from "camelcase";
import { useMemo } from "react";

import {
  FeaturesFlagsQuery,
  FeaturesFlagsQueryVariables,
} from "@/graphql/generated/graphql";
import { FeaturesFlags } from "@/graphql/queries/FeaturesFlags";
import { useGraphQuery } from "@dashboard/library";
import {
  FEATURE_FLAG_NAMES,
  FeaturesFlagsData,
  GetFeaturesFlagsType,
} from "./useGetFeaturesFlags.decl";
/**
 * Hook providing feature flags.
 * @returns an object holding application feature flags
 */
export function useGetFeaturesFlags(): GetFeaturesFlagsType {
  const variables: FeaturesFlagsQueryVariables = {
    names: [...FEATURE_FLAG_NAMES],
  };
  const { data, loading } = useGraphQuery<FeaturesFlagsQuery>(FeaturesFlags, {
    throwError: true,
    variables,
  });

  const featureFlags = useMemo(() => {
    if (!data) {
      return undefined;
    }

    // Mutate the accumulator instead of spreading it. `getFeatureFlags` is a
    // list (REST used to return an object that Apollo pruned to its 5-field
    // selection set), so the full ~500-flag list reaches this hook. A
    // spread-in-reduce copies the accumulator on every iteration — O(n²) — which
    // blocked the main thread for ~10s and froze the page (CI-5795).
    return data.getFeatureFlags.reduce<Record<string, boolean>>(
      (acc, { name, isEnabled }) => {
        acc[camelcase(name)] = isEnabled;
        return acc;
      },
      {}
    ) as FeaturesFlagsData;
  }, [data]);

  return useMemo(() => {
    return {
      ...(featureFlags ?? {}),
      loading,
    };
  }, [featureFlags, loading]);
}
